// Lista de utilizadores: pesquisa (nome, email, ID), filtros por papel e estado, ordenação, páginas e CSV.
import { useApi } from "@ecliptica/api-client/react";
import type { AccountStatus } from "@ecliptica/contexts-accounts";
import { useTranslation } from "@ecliptica/i18n";
import { Avatar, Button, Card, EmptyState, Input, NativeSelect, PageHeader, Skeleton, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ecliptica/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Download, ShieldCheck, Users } from "lucide-react";
import { Link, useSearchParams } from "react-router";
import { useRoleLabel, useRoles } from "../../features/roles/useRoles";
import { RoleBadge, StatusBadge } from "../../features/users/badges";
import { formatDate, useDebounced } from "../../lib/format";

const STATUSES: AccountStatus[] = ["active", "suspended", "banned", "unverified"];
const SORTS = ["created_desc", "created_asc", "name_asc", "last_seen_desc"] as const;
const PAGE = 25;

export function UsersPage() {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const roles = useRoles();
  const roleLabel = useRoleLabel();
  // Os filtros vivem no endereço: dá para partilhar o link de uma pesquisa e o "voltar" do browser funciona.
  const [params, setParams] = useSearchParams();
  const qRaw = params.get("q") ?? "";
  const q = useDebounced(qRaw);
  const role = params.get("role") ?? "";
  const status = (params.get("status") ?? "") as AccountStatus | "";
  const sort = (params.get("sort") ?? "created_desc") as (typeof SORTS)[number];
  const page = Math.max(Number(params.get("page") ?? 1), 1);

  const set = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    if (!("page" in patch)) next.delete("page");
    setParams(next, { replace: true });
  };

  const filters = { q: q || undefined, role: role || undefined, status: status || undefined, sort };
  const list = useQuery({
    queryKey: ["users", filters, page],
    queryFn: async () => {
      const res = await api.v1.admin.users.$get({ query: { ...filters, limit: String(PAGE), offset: String((page - 1) * PAGE) } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    placeholderData: keepPreviousData,
  });
  const pages = Math.max(Math.ceil((list.data?.total ?? 0) / PAGE), 1);
  const csvHref = `/v1/admin/users/export.csv?${new URLSearchParams(Object.entries(filters).filter(([, v]) => v) as [string, string][])}`;

  return (
    <>
      <PageHeader
        title={t("nav.users")}
        description={t("users.description")}
        actions={
          <Button asChild variant="outline">
            <a href={csvHref} download>
              <Download /> {t("users.export")}
            </a>
          </Button>
        }
      />
      <div className="mb-4 flex flex-wrap gap-2">
        <Input type="search" placeholder={t("users.search")} aria-label={t("users.search")} value={qRaw} onChange={(e) => set({ q: e.target.value })} className="w-full sm:w-80" />
        <NativeSelect aria-label={t("users.allRoles")} value={role} onChange={(e) => set({ role: e.target.value })}>
          <option value="">{t("users.allRoles")}</option>
          {(roles.data?.roles ?? []).map((r) => (
            <option key={r.key} value={r.key}>
              {roleLabel(r.key, r.labels)}
            </option>
          ))}
        </NativeSelect>
        <NativeSelect aria-label={t("users.allStatus")} value={status} onChange={(e) => set({ status: e.target.value })}>
          <option value="">{t("users.allStatus")}</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`users.status.${s}`)}
            </option>
          ))}
        </NativeSelect>
        <NativeSelect aria-label="sort" value={sort} onChange={(e) => set({ sort: e.target.value === "created_desc" ? "" : e.target.value })}>
          {SORTS.map((s) => (
            <option key={s} value={s}>
              {t(`users.sort.${s}`)}
            </option>
          ))}
        </NativeSelect>
      </div>
      <Card>
        {list.isPending ? (
          <div className="flex flex-col gap-2 p-4">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !list.data?.items.length ? (
          <EmptyState icon={<Users />} title={t("users.empty")} className="m-4" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("users.col.user")}</TableHead>
                <TableHead>{t("users.col.role")}</TableHead>
                <TableHead>{t("users.col.status")}</TableHead>
                <TableHead className="hidden md:table-cell">{t("users.col.created")}</TableHead>
                <TableHead className="hidden md:table-cell">{t("users.col.lastSeen")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.data.items.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>
                    <Link to={`/users/${u.id}`} className="flex items-center gap-3 hover:text-gold-bright">
                      <Avatar name={u.name} className="size-8" />
                      <span className="min-w-0">
                        <span className="block truncate font-semibold">{u.name}</span>
                        <span className="block truncate text-xs text-muted-foreground">{u.email}</span>
                      </span>
                      {u.twoFactorEnabled && <ShieldCheck className="size-4 text-success" aria-label={t("users.twoFactor")} />}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <RoleBadge role={u.role} />
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={u.status} />
                  </TableCell>
                  <TableCell className="hidden text-xs text-muted-foreground md:table-cell">{formatDate(u.createdAt, i18n.language, false)}</TableCell>
                  <TableCell className="hidden text-xs text-muted-foreground md:table-cell">{u.lastSeen ? formatDate(u.lastSeen, i18n.language) : t("users.never")}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
        <span>{t("users.count", { count: list.data?.total ?? 0 })}</span>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => set({ page: String(page - 1) })}>
            {t("users.prev")}
          </Button>
          <span>{t("users.page", { page, pages })}</span>
          <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => set({ page: String(page + 1) })}>
            {t("users.next")}
          </Button>
        </div>
      </div>
    </>
  );
}
